import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "environments/environment";
import { firstValueFrom } from "rxjs";
import {
  ApplySizeMappingResult,
  BulkRepointScopeDTO,
  FeedBuildColorDTO,
  ProductTypeFeedHealthDTO,
  RepointCandidateColorDTO,
  RetirementScopeDTO,
  StagedFeedManufacturer,
  StagedFeedRunDTO,
  StagedProductColorDTO,
  StagedProductTypeDTO,
  SizeMappingCandidateDTO,
  FeedStyleSummaryDTO,
  RemoveFeedStylesResult,
  StagedStyleDetailDTO,
} from "./staged-feed.models";

/** HTTP client for the staged manufacturer feed admin endpoint. */
/**
 * What the product type editor has on screen, which may not be saved yet. Sent so Feed Health
 * judges the style as it stands: the size list, every feature (for the shape check), and on Edit
 * the SKU and manufacturer when they have been changed.
 */
export interface FeedHealthScreen {
  sizeFeatureId?: number | null;
  featureIds?: number[];
  style?: string | null;
  manufacturerId?: number | null;
}

function withScreen(params: HttpParams, screen: FeedHealthScreen): HttpParams {
  if (screen.sizeFeatureId != null) params = params.set("sizeFeatureId", screen.sizeFeatureId);
  for (const id of screen.featureIds ?? []) params = params.append("featureIds", id);
  return params;
}

@Injectable({ providedIn: "root" })
export class StagedFeedApiService {
  private readonly baseUrl = environment.apiRoot + "api/StagedFeed";

  constructor(private http: HttpClient) {}

  async getManufacturers(): Promise<StagedFeedManufacturer[]> {
    return firstValueFrom(this.http.get<StagedFeedManufacturer[]>(`${this.baseUrl}/Manufacturers`));
  }

  async getRuns(manufacturerId: number): Promise<StagedFeedRunDTO[]> {
    const params = new HttpParams().set("manufacturerId", manufacturerId);
    return firstValueFrom(this.http.get<StagedFeedRunDTO[]>(`${this.baseUrl}/Runs`, { params }));
  }

  async getStyles(manufacturerId: number): Promise<StagedProductTypeDTO[]> {
    const params = new HttpParams().set("manufacturerId", manufacturerId);
    return firstValueFrom(this.http.get<StagedProductTypeDTO[]>(`${this.baseUrl}/Styles`, { params }));
  }

  async getStyleDetail(stagedProductTypeId: number): Promise<StagedStyleDetailDTO> {
    const params = new HttpParams().set("stagedProductTypeId", stagedProductTypeId);
    return firstValueFrom(this.http.get<StagedStyleDetailDTO>(`${this.baseUrl}/StyleDetail`, { params }));
  }

  /**
   * How this product type stands against the feed. Null when the style is not staged.
   *
   * sizeFeatureId is the size scale the editor currently has attached. Pass it whenever one is
   * known: on Add, and after an unsaved feature change, it is the only thing that can say which
   * scale the sizes should be judged against.
   */
  async getProductTypeHealth(
    productTypeId: number,
    screen: FeedHealthScreen = {}
  ): Promise<ProductTypeFeedHealthDTO | null> {
    let params = withScreen(new HttpParams().set("productTypeId", productTypeId), screen);
    if (screen.style != null) params = params.set("style", screen.style);
    if (screen.manufacturerId != null) params = params.set("manufacturerId", screen.manufacturerId);
    return firstValueFrom(this.http.get<ProductTypeFeedHealthDTO>(`${this.baseUrl}/ProductTypeHealth`, { params }));
  }

  /**
   * The same health panel for a style that does not exist in Uniformax yet. Add Product Type has
   * no id to ask with, and the underlying data is keyed by style anyway.
   */
  async getStyleHealth(
    manufacturerId: number,
    style: string,
    screen: FeedHealthScreen = {}
  ): Promise<ProductTypeFeedHealthDTO | null> {
    const params = withScreen(new HttpParams().set("manufacturerId", manufacturerId).set("style", style), screen);
    return firstValueFrom(this.http.get<ProductTypeFeedHealthDTO>(`${this.baseUrl}/StyleHealth`, { params }));
  }

  /**
   * The color-and-size pairs the manufacturer publishes for this style, grouped by color and
   * resolved to our own FeatureChoices.
   *
   * Null means there is nothing to restrict against - no feed, no snapshot, or no size scale - and
   * must be read as "do not restrict" rather than "restrict to nothing".
   */
  async getBuildablePairs(
    productTypeId: number,
    sizeFeatureId?: number | null
  ): Promise<FeedBuildColorDTO[] | null> {
    let params = new HttpParams().set("productTypeId", productTypeId);
    if (sizeFeatureId != null) params = params.set("sizeFeatureId", sizeFeatureId);
    return firstValueFrom(this.http.get<FeedBuildColorDTO[]>(`${this.baseUrl}/BuildablePairs`, { params }));
  }

  /** Colors the manufacturer publishes for this product type's style. */
  async getStyleColorsForProductType(productTypeId: number): Promise<StagedProductColorDTO[] | null> {
    const params = new HttpParams().set("productTypeId", productTypeId);
    return firstValueFrom(
      this.http.get<StagedProductColorDTO[]>(`${this.baseUrl}/StyleColorsForProductType`, { params })
    );
  }

  /** Fetch one style live and stage it. Synchronous on the server. */
  async stageStyle(manufacturerId: number, style: string): Promise<StagedFeedRunDTO> {
    const params = new HttpParams().set("manufacturerId", manufacturerId).set("style", style);
    return firstValueFrom(this.http.get<StagedFeedRunDTO>(`${this.baseUrl}/StageStyle`, { params }));
  }

  /** Every color this manufacturer's live styles carry, with how far the feed agrees. */
  async getRepointCandidates(manufacturerId: number): Promise<RepointCandidateColorDTO[]> {
    const params = new HttpParams().set("manufacturerId", manufacturerId);
    return firstValueFrom(
      this.http.get<RepointCandidateColorDTO[]>(`${this.baseUrl}/RepointCandidates`, { params })
    );
  }

  /** Which styles could move from one color to another. Read-only — the client applies the change. */
  async getBulkRepointScope(
    manufacturerId: number, fromFeatureChoiceId: number, toFeatureChoiceId: number
  ): Promise<BulkRepointScopeDTO> {
    const params = new HttpParams()
      .set("manufacturerId", manufacturerId)
      .set("fromFeatureChoiceId", fromFeatureChoiceId)
      .set("toFeatureChoiceId", toFeatureChoiceId);
    return firstValueFrom(this.http.get<BulkRepointScopeDTO>(`${this.baseUrl}/BulkRepointScope`, { params }));
  }

  /**
   * Sizes the feed publishes that no Uniformax choice corresponds to yet, with a proposed answer.
   *
   * reopen names sizes to put back in the queue although they ARE answered, on one size list. A
   * hand answer that is wrong but valid builds products, so the queue - which looks for sizes
   * with no answer - can never find it. Named sizes only, so the queue stays a list of work.
   */
  async getSizeMappingCandidates(
    manufacturerId: number,
    reopen?: { featureId: number; feedSizes: string[] }
  ): Promise<SizeMappingCandidateDTO[]> {
    let params = new HttpParams().set("manufacturerId", manufacturerId);
    if (reopen && reopen.feedSizes.length) {
      params = params.set("reopenFeatureId", reopen.featureId);
      // Repeated key, not a joined string: a size may legitimately contain a comma.
      reopen.feedSizes.forEach(s => (params = params.append("reopenSizes", s)));
    }
    return firstValueFrom(
      this.http.get<SizeMappingCandidateDTO[]>(`${this.baseUrl}/SizeMappingCandidates`, { params })
    );
  }

  /** What Remove Feed Styles would delete for this manufacturer. */
  async getFeedStyleSummary(manufacturerId: number): Promise<FeedStyleSummaryDTO> {
    const params = new HttpParams().set("manufacturerId", manufacturerId);
    return firstValueFrom(this.http.get<FeedStyleSummaryDTO>(`${this.baseUrl}/FeedStyleSummary`, { params }));
  }

  /** Delete every feed style read for this manufacturer and set its feed status to None. */
  async removeFeedStyles(manufacturerId: number): Promise<RemoveFeedStylesResult> {
    return firstValueFrom(
      this.http.post<RemoveFeedStylesResult>(`${this.baseUrl}/RemoveFeedStyles`, { ManufacturerId: manufacturerId })
    );
  }

  /** Feed sizes already matched on this size list by a choice spelled exactly as the feed spells it. */
  async getExactSizeMatches(manufacturerId: number, featureId: number): Promise<string[]> {
    const params = new HttpParams().set("manufacturerId", manufacturerId).set("featureId", featureId);
    return firstValueFrom(this.http.get<string[]>(`${this.baseUrl}/ExactSizeMatches`, { params }));
  }

  /**
   * Run the name matcher over one staged style again. Does not call the manufacturer.
   *
   * Needed after anything that changes the Uniformax side while the style is on screen. The
   * staged row holds the correspondence, and only the matcher writes it, so creating the very
   * color the feed asked for used to leave the cell still reading Create.
   */
  async reresolveStyle(stagedProductTypeId: number): Promise<number> {
    return firstValueFrom(
      this.http.post<number>(`${this.baseUrl}/ReresolveStyle`, { StagedProductTypeId: stagedProductTypeId })
    );
  }

  /** Answer one size question across every style using that size feature. */
  async applySizeMapping(body: {
    manufacturerId: number;
    feedSize: string;
    apparelStyle: string | null;
    featureId: number;
    featureChoiceId: number | null;
  }): Promise<ApplySizeMappingResult> {
    return firstValueFrom(
      this.http.post<ApplySizeMappingResult>(`${this.baseUrl}/ApplySizeMapping`, {
        ManufacturerId: body.manufacturerId,
        FeedSize: body.feedSize,
        ApparelStyle: body.apparelStyle,
        FeatureId: body.featureId,
        FeatureChoiceId: body.featureChoiceId,
      })
    );
  }

  /**
   * Feed styles and products the feed says have to leave the catalogue. Read-only — the client
   * applies the retirement through Breeze.
   */
  async getRetirementCandidates(manufacturerId: number): Promise<RetirementScopeDTO> {
    const params = new HttpParams().set("manufacturerId", manufacturerId);
    return firstValueFrom(
      this.http.get<RetirementScopeDTO>(`${this.baseUrl}/RetirementCandidates`, { params })
    );
  }

  /** Queue a full sweep to Hangfire. Returns the job id; poll getRuns for progress. */
  async startSweep(manufacturerId: number, maxStyles: number | null): Promise<string> {
    let params = new HttpParams().set("manufacturerId", manufacturerId);
    if (maxStyles != null) params = params.set("maxStyles", maxStyles);
    return firstValueFrom(
      this.http.get(`${this.baseUrl}/StartSweep`, { params, responseType: "text" })
    );
  }
}
